import { forwardRef, type AnchorHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { localeFromPath } from '@/lib/locale-url';
import { CONSULTATION_URL } from '@/config/booking';
import { trackEvent } from '@/lib/analytics';

interface BookingLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Where the button sits, for analytics (e.g. "navigation-desktop"). */
  location: string;
}

// The label follows the URL (/fr/* is French) rather than i18n state, so a
// button rendered before LocaleSync switches language never shows the wrong one.
const useBookingT = () => {
  const { t } = useTranslation();
  const lng = localeFromPath(useLocation().pathname);
  return (key: string) => t(key, { lng });
};

/**
 * External link to the Cal.com consultation booking page. Opens in a new tab;
 * the accessible name keeps the visible label and adds the duration and the
 * new-tab notice. Renders the default label when no children are passed.
 */
const BookingLink = forwardRef<HTMLAnchorElement, BookingLinkProps>(
  ({ location, onClick, children, ...rest }, ref) => {
    const t = useBookingT();
    return (
      <a
        ref={ref}
        href={CONSULTATION_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('booking.aria')}
        data-booking-link={location}
        onClick={(e) => {
          trackEvent('consultation_booking_click', { cta_location: location, destination: 'cal.com' });
          onClick?.(e);
        }}
        {...rest}
      >
        {children ?? t('booking.cta')}
      </a>
    );
  },
);
BookingLink.displayName = 'BookingLink';

/** The translated "Book a Free Consultation" label, for buttons with icons. */
export const BookingText = () => {
  const t = useBookingT();
  return <>{t('booking.cta')}</>;
};

export default BookingLink;
